import { saveAs } from 'file-saver';
import moment from 'moment';
import {
  ARCHIVO_SUPER_EJECUTIVO,
  CLASE_FUERA_DE_IMAGEN_SUPER_EJECUTIVO,
  COLOR_BLANCO_GRAFICA_SUPER_EJECUTIVO,
  EXCEL_SUPER_EJECUTIVO,
  HOLGURA_IMAGEN_TABLA_SUPER_EJECUTIVO,
  MARGEN_IMAGEN_SUPER_EJECUTIVO,
  PIXELES_POR_PUNTO_IMAGEN_SUPER_EJECUTIVO,
  TEXTOS_DESCARGA_SUPER_EJECUTIVO,
} from '../super-ejecutivo.constants';
import { CeldaExcelSuperEjecutivo, FormatoDescargaSuperEjecutivo, ITablaExcelSuperEjecutivo } from '../super-ejecutivo.interface';

/**
 * Descarga de lo que muestra el Super Ejecutivo (gráficas y tablas) como imagen o como Excel. Todo se arma en el
 * navegador con lo que ya se ve; las librerías se cargan al descargar para no pesar en la carga del panel.
 */

const EXCEL = EXCEL_SUPER_EJECUTIVO;

/** Un texto como parte de un nombre de archivo: sin acentos, sin espacios y sin caracteres especiales. */
function parteDeNombre(texto: string): string {
  const { caracteresNoPermitidos, separador } = ARCHIVO_SUPER_EJECUTIVO;

  return texto
    .normalize('NFD')
    // Las marcas de acento quedan aparte al descomponer: "á" pasa a "a" y "ñ" a "n".
    .replace(/[̀-ͯ]/g, '')
    .replace(caracteresNoPermitidos, separador)
    .replace(new RegExp(`^${separador}+|${separador}+$`, 'g'), '');
}

/** Nombre del archivo de una gráfica: su título y el día de la descarga, ej. `Cuentas_por_municipio_2026-10-29.png`. */
export function nombreArchivoGraficaSuperEjecutivo(titulo: string, fecha: Date, formato: FormatoDescargaSuperEjecutivo): string {
  const base = parteDeNombre(titulo) || ARCHIVO_SUPER_EJECUTIVO.respaldoGrafica;

  return `${base}${ARCHIVO_SUPER_EJECUTIVO.separador}${moment(fecha).format(ARCHIVO_SUPER_EJECUTIVO.formatoFecha)}.${formato}`;
}

/** Nombre del archivo de una tabla, que no trae título: "Tabla" con el día y la hora, ej. `Tabla_2026-10-30_11-45.xlsx`. */
export function nombreArchivoTablaSuperEjecutivo(fecha: Date, formato: FormatoDescargaSuperEjecutivo): string {
  const { baseTabla, separador, formatoFechaHora } = ARCHIVO_SUPER_EJECUTIVO;

  return `${baseTabla}${separador}${moment(fecha).format(formatoFechaHora)}.${formato}`;
}

/**
 * Una celda de texto como va al Excel: una cifra (con separador de miles y decimales, como las escribe el Super
 * Ejecutivo) se guarda como número para poder operar con ella; lo demás queda como texto. Un número con ceros a la
 * izquierda o más largo de lo que Excel guarda exacto (un folio, un teléfono) también queda como texto.
 */
export function celdaExcelSuperEjecutivo(texto: string): CeldaExcelSuperEjecutivo {
  const limpio = texto.trim();

  if (!/^-?\d{1,3}(,\d{3})*(\.\d+)?$|^-?\d+(\.\d+)?$/.test(limpio)) {
    return limpio;
  }

  const sinMiles = limpio.replace(/,/g, '');
  const digitos = sinMiles.replace(/^-/, '').replace('.', '');

  if (/^-?0\d/.test(sinMiles) || digitos.length > EXCEL.digitosMaximosNumero) {
    return limpio;
  }

  return Number(sinMiles);
}

/**
 * Dibuja un elemento del panel en un lienzo de `ancho` × `alto` px de pantalla: lo mismo que se ve, sobre fondo blanco
 * y sin lo marcado con `CLASE_FUERA_DE_IMAGEN_SUPER_EJECUTIVO`.
 */
async function lienzoDeElemento(elemento: HTMLElement, ancho: number, alto: number): Promise<HTMLCanvasElement> {
  const { toCanvas } = await import('html-to-image');

  return toCanvas(elemento, {
    backgroundColor: COLOR_BLANCO_GRAFICA_SUPER_EJECUTIVO,
    pixelRatio: PIXELES_POR_PUNTO_IMAGEN_SUPER_EJECUTIVO,
    width: ancho,
    height: alto,
    // El margen separa los mensajes del chat; en la imagen correría el contenido.
    style: { margin: '0' },
    filter: (nodo) => !(nodo instanceof HTMLElement && nodo.classList.contains(CLASE_FUERA_DE_IMAGEN_SUPER_EJECUTIVO)),
  });
}

/** Guarda como PNG la esquina superior izquierda de `contenido`, de `ancho` × `alto` px, con un margen blanco alrededor. */
async function guardarImagenConMargen(contenido: HTMLCanvasElement, ancho: number, alto: number, nombre: string): Promise<void> {
  // El margen se pinta en un lienzo aparte: un margen en el elemento capturado quedaría transparente.
  const margen = MARGEN_IMAGEN_SUPER_EJECUTIVO * PIXELES_POR_PUNTO_IMAGEN_SUPER_EJECUTIVO;
  const lienzo = document.createElement('canvas');
  lienzo.width = ancho + margen * 2;
  lienzo.height = alto + margen * 2;
  const dibujo = lienzo.getContext('2d');

  if (!dibujo) {
    throw new Error(TEXTOS_DESCARGA_SUPER_EJECUTIVO.NO_DESCARGADA);
  }

  dibujo.fillStyle = COLOR_BLANCO_GRAFICA_SUPER_EJECUTIVO;
  dibujo.fillRect(0, 0, lienzo.width, lienzo.height);
  dibujo.drawImage(contenido, 0, 0, ancho, alto, margen, margen, ancho, alto);

  const imagen = await new Promise<Blob | null>((resolver) => lienzo.toBlob(resolver, 'image/png'));

  if (!imagen) {
    throw new Error(TEXTOS_DESCARGA_SUPER_EJECUTIVO.NO_DESCARGADA);
  }

  saveAs(imagen, nombre);
}

/**
 * Guarda un elemento del panel como PNG, con un margen blanco alrededor. Toma el ancho completo del contenido, por si
 * una parte se desplaza de lado.
 */
export async function descargarImagenSuperEjecutivo(elemento: HTMLElement, nombre: string): Promise<void> {
  const contenido = await lienzoDeElemento(elemento, elemento.scrollWidth, elemento.scrollHeight);

  await guardarImagenConMargen(contenido, contenido.width, contenido.height, nombre);
}

/** El pixel `i` (en RGBA) es del fondo blanco de la imagen. */
const esFondo = (pixeles: Uint8ClampedArray, i: number) => pixeles[i] === 255 && pixeles[i + 1] === 255 && pixeles[i + 2] === 255;

/** Último pixel que no es fondo en una tira de pixeles, o -1 si toda es fondo. */
function ultimoNoFondo(pixeles: Uint8ClampedArray): number {
  for (let i = pixeles.length / 4 - 1; i >= 0; i--) {
    if (!esFondo(pixeles, i * 4)) {
      return i;
    }
  }

  return -1;
}

/**
 * Ancho y alto de la tabla dibujada en `contenido`, desde su esquina superior izquierda: hasta donde llega el fondo del
 * encabezado (a lo ancho) y la línea inferior de la última fila (a lo alto). Si no los encuentra, todo el lienzo.
 */
function tamanoDeTablaDibujada(contenido: HTMLCanvasElement): { ancho: number; alto: number } {
  const dibujo = contenido.getContext('2d');

  if (!dibujo) {
    return { ancho: contenido.width, alto: contenido.height };
  }

  // A un punto de pantalla del borde: dentro del encabezado y dentro de la primera columna.
  const dentro = PIXELES_POR_PUNTO_IMAGEN_SUPER_EJECUTIVO;
  const ancho = ultimoNoFondo(dibujo.getImageData(0, dentro, contenido.width, 1).data) + 1;
  const alto = ultimoNoFondo(dibujo.getImageData(dentro, 0, 1, contenido.height).data) + 1;

  return { ancho: ancho || contenido.width, alto: alto || contenido.height };
}

/**
 * Guarda una tabla del panel como PNG, completa aunque en el panel se desplace de lado y sin importar hasta dónde la
 * haya desplazado el usuario. Se toma la tabla sola, sin el contenedor que la desplaza: en la imagen ese contenedor
 * dibujaría sus barras de desplazamiento y cortaría la tabla. El texto en la imagen puede salir unos píxeles más ancho
 * que en pantalla, así que se dibuja con holgura y se recorta al borde de la tabla.
 */
export async function descargarImagenTablaSuperEjecutivo(tabla: HTMLTableElement, nombre: string): Promise<void> {
  const conHolgura = (medida: number) => Math.ceil(medida * (1 + HOLGURA_IMAGEN_TABLA_SUPER_EJECUTIVO));
  const contenido = await lienzoDeElemento(tabla, conHolgura(tabla.scrollWidth), conHolgura(tabla.scrollHeight));
  const { ancho, alto } = tamanoDeTablaDibujada(contenido);

  await guardarImagenConMargen(contenido, ancho, alto, nombre);
}

/** Ancho de cada columna según su texto más largo, con un mínimo y un máximo para que la hoja se lea. */
function anchosDeColumna(tabla: ITablaExcelSuperEjecutivo): number[] {
  return tabla.columnas.map((columna, i) => {
    const largos = [columna, ...tabla.filas.map((fila) => fila[i])].map((celda) => String(celda ?? '').length);
    const { anchoMinimo, anchoMaximo, margenAncho } = EXCEL;

    return Math.min(anchoMaximo, Math.max(anchoMinimo, ...largos) + margenAncho);
  });
}

/**
 * Guarda el Excel de una hoja: el encabezado y una fila en blanco (si hay encabezado), los títulos de columna en
 * negritas y las filas.
 */
export async function descargarExcelSuperEjecutivo(tabla: ITablaExcelSuperEjecutivo, nombre: string, hoja: string): Promise<void> {
  // exceljs es CommonJS: el build de Kobra Admin lo expone solo como `default`, sin exportaciones con nombre.
  const exceljs: typeof import('exceljs') & { default?: typeof import('exceljs') } = await import('exceljs');
  const { Workbook } = exceljs.default ?? exceljs;
  const libro = new Workbook();
  const hojaExcel = libro.addWorksheet(hoja);

  if (tabla.encabezado.length > 0) {
    tabla.encabezado.forEach((linea) => hojaExcel.addRow([linea]));
    hojaExcel.getRow(1).font = { bold: true, size: EXCEL.tamanoTitulo };
    hojaExcel.addRow([]);
  }

  hojaExcel.addRow(tabla.columnas).font = { bold: true };
  tabla.filas.forEach((fila) => {
    const renglon = hojaExcel.addRow(fila);

    if (tabla.columnaPorcentaje !== undefined) {
      renglon.getCell(tabla.columnaPorcentaje + 1).numFmt = EXCEL.formatoPorcentaje;
    }
  });
  anchosDeColumna(tabla).forEach((ancho, i) => (hojaExcel.getColumn(i + 1).width = ancho));

  const contenido = await libro.xlsx.writeBuffer();

  saveAs(new Blob([contenido], { type: EXCEL.tipo }), nombre);
}
